import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { asId } from '@shared/brand';
import type { AuthenticatedUser, SignInError } from '@application/ports';
import type { Session } from '@application/use-cases/session';
import type { Person } from '@domain/entities/Person';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@domain/entities/Person';
import { skipAccountValidation } from '@infrastructure/bootstrapMode';
import { getServices } from '@infrastructure/services';
import { clearSessionEnded, markSessionEnded, outcomeForUnreadableRecord } from '../sessionEnd';
import { signInErrorMessage } from '@infrastructure/firebase/FirebaseAuthGateway';
import { useRuntimeConfig } from './RuntimeConfigProvider';

/**
 * The signed-in session.
 *
 * Resolving a session is a two-step affair, and the intermediate state is a real
 * one the UI has to render rather than an edge case:
 *
 *  1. Firebase reports an authenticated user. On the corporate domain, this always
 *     succeeds — the blocking function already rejected anything else.
 *  2. The `people` record and the custom claims decide whether that user can *do*
 *     anything. A new account is `pending`, holds no role, and reads nothing.
 *
 * So "signed in but not yet authorised" is the normal first experience of every new
 * hire, and it gets its own status rather than being treated as a failure.
 */

export type SessionStatus =
  | { readonly kind: 'loading' }
  | { readonly kind: 'signed-out' }
  /** Authenticated on the corporate domain, awaiting an administrator's validation. */
  | { readonly kind: 'pending-validation'; readonly user: AuthenticatedUser }
  /** Authenticated but deactivated by HR. */
  | { readonly kind: 'inactive'; readonly user: AuthenticatedUser }
  /**
   * Autenticada y con claims válidas, pero su ficha no se pudo leer por la red.
   *
   * Existe para no mentir sobre la cuenta. Antes este caso caía en
   * `pending-validation`, y un superusuario activo veía «tu cuenta está en
   * camino» al despertar el portátil. Nada se sabe de la cuenta cuando falla la
   * red, así que la pantalla no afirma nada de ella: ofrece reintentar.
   */
  | { readonly kind: 'unreachable'; readonly user: AuthenticatedUser }
  | { readonly kind: 'ready'; readonly session: Session; readonly person: Person };

interface SessionState {
  readonly status: SessionStatus;
  readonly signIn: () => Promise<void>;
  /** QA's password door. Offered only when the build enables it. */
  readonly signInWithPassword: (email: string, password: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  /** Re-reads claims after an administrator validates the account. */
  readonly refresh: () => Promise<void>;
  readonly signInError: string | null;
  readonly signingIn: boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export const SessionProvider = ({ children }: { children: ReactNode }): ReactNode => {
  const config = useRuntimeConfig();
  /*
   * The query cache outlives a session, and that is a problem in two sizes.
   *
   * The `QueryClient` is a module singleton with `staleTime: 60s` and
   * `gcTime: 5min`, so it survives a sign-out inside the same page load. The small
   * consequence is what sent us here: after the display name was repaired at
   * sign-in the sidebar showed it — that comes straight from this provider — while
   * «Mi perfil» kept serving its cached copy from before, because the query was
   * still inside its stale window and never refetched.
   *
   * The large one is the reason this is a clear rather than an invalidate: on a
   * shared device, the next person to sign in would be handed the previous one's
   * profile, balance, requests and emergency contacts out of that same cache
   * before any refetch landed. `clearCursorCache()` already guards the same seam
   * for pagination; this closes the rest of it.
   */
  const queryClient = useQueryClient();
  /** The uid the cache currently holds data for. `undefined` while signed out. */
  const lastUid = useRef<string | undefined>(undefined);
  const [status, setStatus] = useState<SessionStatus>({ kind: 'loading' });
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  /**
   * Builds a session for an account that has not been validated yet.
   *
   * Only reachable with `VITE_SKIP_ACCOUNT_VALIDATION` on. Looks the person up by
   * email — the record exists as `pending` because the blocking function creates it
   * on first sign-in — and falls back to a minimal `empleado` session when even that
   * is missing, which is the true empty-database case.
   *
   * Returns null when the lookup fails outright, so the caller falls through to the
   * normal pending screen rather than inventing a session on top of a broken read.
   */
  const resolveProvisionalSession = useCallback(
    async (user: AuthenticatedUser): Promise<SessionStatus | null> => {
      const services = getServices();

      const byEmail = await services.people.getByEmail(user.email);

      if (byEmail.ok) {
        return {
          kind: 'ready',
          person: byEmail.value,
          session: {
            personId: byEmail.value.id,
            hireDate: byEmail.value.hireDate,
            displayName: byEmail.value.displayName,
            email: byEmail.value.email,
            role: byEmail.value.role,
            areaId: byEmail.value.areaId,
            subAreaId: byEmail.value.subAreaId,
            config,
          },
        };
      }

      if (byEmail.error.kind !== 'not-found') {
        services.logger.warn('bootstrap lookup failed; falling back to the pending screen', {
          error: byEmail.error,
        });
        return null;
      }

      // Nothing in `people` at all — an entirely empty database. A minimal employee
      // session lets the portal be opened and configured; every read will still be
      // governed by the security rules, so this grants visibility, not authority.
      services.logger.warn('bootstrap: no person record; using a provisional session', {
        email: user.email,
      });

      return {
        kind: 'ready',
        person: provisionalPerson(user),
        session: {
          personId: asId<'PersonId'>(user.uid),
          hireDate: null,
          displayName: user.displayName.length > 0 ? user.displayName : user.email,
          email: user.email,
          role: 'empleado',
          areaId: asId<'AreaId'>(''),
          subAreaId: null,
          config,
        },
      };
    },
    [config],
  );

  /**
   * Turns an authenticated user into a session, or into one of the not-yet-usable
   * states. Reads the `people` record because the claims alone do not carry the
   * display name, sub area or hire date the UI needs.
   */
  const resolve = useCallback(
    async (user: AuthenticatedUser | null): Promise<SessionStatus> => {
      if (user === null) return { kind: 'signed-out' };

      const services = getServices();

      // A deactivated account is turned away even in bootstrap mode: that is HR
      // revoking access on purpose, not the absence of an approval.
      if (user.claims.status === 'inactive') return { kind: 'inactive', user };

      /**
       * Cold start. Without an administrator nobody can be validated, and without a
       * validated account there is no administrator — so the flag lets the first
       * corporate sign-in through with whatever role their record carries.
       *
       * The domain gate is untouched; this only skips the human approval step.
       */
      if (skipAccountValidation()) {
        const provisional = await resolveProvisionalSession(user);
        if (provisional !== null) return provisional;
      }

      // No role claim means the account has not been validated. Do not attempt to
      // read `people` — the rules would deny it and log a confusing error.
      if (user.claims.role === null || user.claims.personId === null) {
        return { kind: 'pending-validation', user };
      }
      if (user.claims.status !== 'active') return { kind: 'pending-validation', user };

      const person = await services.people.getById(user.claims.personId);

      if (!person.ok) {
        /*
         * Las claims dicen «activa» y la ficha no se leyó. **Qué significa depende
         * de por qué**, y aplanarlo todo en `pending-validation` era el defecto.
         *
         * Esa rama le decía a un superusuario activo que su cuenta «está en
         * camino» y que RR.HH. tenía que validarla, porque su token había caducado
         * durante la noche o la red aún no volvía al despertar el portátil. Con las
         * claims intactas, éste era el único camino a esa pantalla. Ver
         * `outcomeForUnreadableRecord`.
         */
        const outcome = outcomeForUnreadableRecord(person.error.kind);
        services.logger.warn('claims present but person record unavailable', {
          personId: user.claims.personId,
          error: person.error,
          outcome,
        });

        if (outcome === 'expired') {
          /* La sesión ya no sirve: se cierra y la pantalla de acceso lo explica.
             Dejarla abierta sería un portal a medio pintar rechazando cada
             lectura. */
          markSessionEnded('expired');
          await services.auth.signOut();
          return { kind: 'signed-out' };
        }
        if (outcome === 'unreachable') return { kind: 'unreachable', user };
        return { kind: 'pending-validation', user };
      }

      return {
        kind: 'ready',
        person: person.value,
        session: {
          personId: person.value.id,
          hireDate: person.value.hireDate,
          displayName: person.value.displayName,
          email: person.value.email,
          // Already narrowed to a valid role by the codec, which falls back to the
          // least privileged one rather than leaving it unset.
          role: person.value.role,
          areaId: person.value.areaId,
          subAreaId: person.value.subAreaId,
          config,
        },
      };
    },
    [config, resolveProvisionalSession],
  );

  /*
   * `resolve` in a ref, and the subscription effect below with a stable dependency
   * list. This is not a style preference; it is the fix for a hang.
   *
   * `resolve` closes over `config`, so it gets a new identity on every
   * configuration snapshot — and the listener delivers at least two on a cold
   * load, one from the persisted cache and one from the server. With `resolve` in
   * the dependency array, each of those tore down the effect and re-subscribed,
   * and the cleanup sets `active = false`, so **a session resolution still in
   * flight was thrown away**. `resolve` awaits `people.getById()`, which on the
   * first load after sign-in is a cold round trip: the config snapshot landed
   * mid-read, the result was discarded, the new subscription started the same read
   * again, and the portal sat on «Cargando el portal» — or on a section spinner —
   * for as long as that raced. A reload made it fast because by then both the
   * configuration and the person record came from IndexedDB and resolved before
   * anything could invalidate them.
   *
   * The session does not lose configuration changes by dropping the dependency:
   * the effect below re-applies `config` to a ready session, which is what it was
   * written for.
   */
  const resolveRef = useRef(resolve);
  resolveRef.current = resolve;

  // Reacts to sign-in and sign-out.
  useEffect(() => {
    const services = getServices();
    let active = true;

    const unsubscribe = services.auth.onAuthStateChanged((user) => {
      /*
       * Whose data the cache holds is decided here, not in `signOut`: an account
       * can change without anyone pressing it — a session expiring, a second tab
       * signing in as somebody else. Comparing the uid catches every one of those
       * and leaves an ordinary token refresh, which reports the same uid, alone.
       */
      if (user?.uid !== lastUid.current) {
        lastUid.current = user?.uid;
        queryClient.clear();
      }

      void resolveRef.current(user).then((next) => {
        /* Entró: el aviso de «te cerramos la sesión» ya se leyó. Si se quedara,
           reaparecería la próxima vez que esta persona cierre sesión por su
           cuenta, contándole algo que ya no pasó. */
        if (next.kind === 'ready') clearSessionEnded();
        // Guards against a resolution landing after the effect was torn down, which
        // would otherwise set state on an unmounted tree. Now that the effect runs
        // once, this only fires on a real unmount.
        if (active) setStatus(next);
      });
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [queryClient]);

  /**
   * Re-derives the session when the configuration changes.
   *
   * Without this, a superuser editing the permission matrix would not see it apply
   * to their own session until they reloaded — the `Session` carries a snapshot of
   * the config, so it has to be rebuilt when that snapshot changes.
   */
  useEffect(() => {
    setStatus((current) =>
      current.kind === 'ready'
        ? { ...current, session: { ...current.session, config } }
        : current,
    );
  }, [config]);

  const signIn = useCallback(async (): Promise<void> => {
    setSigningIn(true);
    setSignInError(null);

    const services = getServices();
    const result = await services.auth.signInWithGoogle(domainHint());

    if (!result.ok) {
      /*
       * El error real, a la consola, antes de convertirlo en una frase.
       *
       * `translateSignInError` guarda el código y el mensaje de Firebase en
       * `detail`, y la pantalla sólo enseña un texto genérico. Sin este registro
       * el detalle se perdía siempre: un acceso que fallaba «a veces» no dejaba
       * nada con qué diagnosticarlo, y hubo que deducir la causa midiendo
       * funciones hermanas porque de la fallida no quedaba ni el código.
       */
      if (result.error.kind !== 'popup-closed') {
        services.logger.warn('sign-in failed', { error: result.error });
      }
      // A closed popup is the user changing their mind, not an error worth shouting
      // about; everything else gets a message.
      setSignInError(
        result.error.kind === 'popup-closed' ? null : messageFor(result.error),
      );
    }
    setSigningIn(false);
  }, []);

  const signInWithPassword = useCallback(
    async (email: string, password: string): Promise<void> => {
      setSigningIn(true);
      setSignInError(null);

      const result = await getServices().auth.signInWithPassword(email, password);
      if (!result.ok) setSignInError(messageFor(result.error));
      setSigningIn(false);
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await getServices().auth.signOut();
    // Belt and braces with the uid check above: this runs before the listener
    // fires, so nothing cached is on screen for even a frame after «Salir».
    queryClient.clear();
    setStatus({ kind: 'signed-out' });
    setSignInError(null);
  }, [queryClient]);

  const refresh = useCallback(async (): Promise<void> => {
    const result = await getServices().auth.refreshClaims();
    if (!result.ok) return;
    const next = await resolve(result.value);
    if (next.kind === 'ready') clearSessionEnded();
    setStatus(next);
  }, [resolve]);

  const value = useMemo<SessionState>(
    () => ({ status, signIn, signInWithPassword, signOut, refresh, signInError, signingIn }),
    [status, signIn, signInWithPassword, signOut, refresh, signInError, signingIn],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSessionState = (): SessionState => {
  const state = useContext(SessionContext);
  if (state === null) {
    throw new Error('useSession debe usarse dentro de <SessionProvider>.');
  }
  return state;
};

/**
 * The active session. Throws when there is none.
 *
 * Intended for use below the auth gate, where the session is guaranteed. A screen
 * that might render signed-out should read `useSessionState()` and branch instead.
 */
export const useSession = (): Session => {
  const { status } = useSessionState();
  if (status.kind !== 'ready') {
    throw new Error('useSession se llamó sin una sesión activa. Usa useSessionState().');
  }
  return status.session;
};

/** The domain hint for Google's account chooser. A convenience, not a control. */
const domainHint = (): string => import.meta.env.VITE_CORPORATE_DOMAIN;

/**
 * A `Person` shaped from the auth profile alone, for the empty-database case.
 *
 * Never written anywhere — it exists only so the shell has a name and a role to
 * render while the real record is created.
 */
const provisionalPerson = (user: AuthenticatedUser): Person => ({
  id: asId<'PersonId'>(user.uid),
  uid: user.uid,
  /* Sin excepciones: este registro no se escribe, así que no puede tenerlas. */
  documents: {},
  workMode: null,
  computerEquipment: '',
  extraPermissions: [],
  revokedPermissions: [],
  birthDate: null,
  shirtSize: null,
  parentRole: null,
  // Provisional y sin partes: este registro no se escribe en ningún lado.
  nameParts: null,
  positionId: null,
  termination: null,
  email: user.email,
  displayName: user.displayName.length > 0 ? user.displayName : user.email,
  areaId: asId<'AreaId'>(''),
  subAreaId: null,
  role: 'empleado',
  status: 'active',
  hireDate: null,
  entitledDaysOverride: null,
  photoURL: user.photoURL,
  notificationPrefs: DEFAULT_NOTIFICATION_PREFERENCES,
  createdAt: new Date(),
  updatedAt: new Date(),
  schemaVersion: 1,
});

const messageFor = (error: SignInError): string => signInErrorMessage(error);
